import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../lib/apiClient';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { BookOpen, Plus, Trash2, Loader2, Edit2, X } from 'lucide-react';

const BlogManager = () => {
  const { toast } = useToast();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    date: '',
    read_time: '',
    category: '',
    tagsText: '',
    author: '',
    content: '',
    published: false,
  });

  const parseTags = (value) =>
    value
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);

  const serializeTags = (value = []) => value.join(', ');

  const loadPosts = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.get('/api/admin/blog');
      setPosts(data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load blog posts', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title || '',
      excerpt: post.excerpt || '',
      date: post.date || '',
      read_time: post.read_time || '',
      category: post.category || '',
      tagsText: serializeTags(post.tags || []),
      author: post.author || '',
      content: post.content || '',
      published: Boolean(post.published),
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingPost(null);
    setFormData({
      title: '',
      excerpt: '',
      date: '',
      read_time: '',
      category: '',
      tagsText: '',
      author: '',
      content: '',
      published: false,
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title: formData.title,
        excerpt: formData.excerpt,
        date: formData.date,
        read_time: formData.read_time,
        category: formData.category,
        tags: parseTags(formData.tagsText),
        author: formData.author,
        content: formData.content,
        published: Boolean(formData.published),
      };
      if (editingPost) {
        await apiClient.put(`/api/admin/blog/${editingPost.id}`, payload);
        toast({ title: 'Success', description: 'Post updated' });
      } else {
        await apiClient.post('/api/admin/blog', payload);
        toast({ title: 'Success', description: 'Post created' });
      }
      await loadPosts();
      handleCancel();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Operation failed', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this blog post?')) return;
    try {
      await apiClient.delete(`/api/admin/blog/${id}`);
      toast({ title: 'Success', description: 'Post deleted' });
      await loadPosts();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete', variant: 'destructive' });
    }
  };

  const newTemplate = {
    title: 'New Post',
    excerpt: '',
    date: new Date().toISOString().split('T')[0],
    read_time: '5 min',
    category: 'Backend',
    tagsText: '',
    author: 'Sushant Kumar Yadav',
    content: '',
    published: false,
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-cyan-500" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="blog-manager">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BookOpen className="w-8 h-8 text-cyan-500" />
          <h1 className="text-3xl font-bold text-white">Blog Management</h1>
        </div>
        {!isEditing && (
          <Button
            onClick={() => {
              setFormData(newTemplate);
              setIsEditing(true);
            }}
            className="bg-cyan-500 hover:bg-cyan-600 text-black"
          >
            <Plus className="w-4 h-4 mr-2" />Add Post
          </Button>
        )}
      </div>

      {isEditing && (
        <div className="bg-neutral-900 border border-cyan-500/50 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-white">{editingPost ? 'Edit Post' : 'Add New Post'}</h2>
            <Button onClick={handleCancel} variant="ghost" size="sm"><X className="w-5 h-5" /></Button>
          </div>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-neutral-300">Title</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-neutral-300">Category</label>
                <Input
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-neutral-300">Excerpt</label>
              <Textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                className="bg-neutral-800 border-neutral-700 text-white"
                rows={3}
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm text-neutral-300">Date</label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-neutral-300">Read Time</label>
                <Input
                  value={formData.read_time}
                  onChange={(e) => setFormData({ ...formData, read_time: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-neutral-300">Author</label>
                <Input
                  value={formData.author}
                  onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                  className="bg-neutral-800 border-neutral-700 text-white"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm text-neutral-300">Tags (comma separated)</label>
              <Input
                value={formData.tagsText}
                onChange={(e) => setFormData({ ...formData, tagsText: e.target.value })}
                className="bg-neutral-800 border-neutral-700 text-white"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm text-neutral-300">Content</label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                className="bg-neutral-800 border-neutral-700 text-white"
                rows={10}
                required
              />
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={formData.published}
                onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                className="h-4 w-4"
              />
              <span className="text-sm text-neutral-400">Publish immediately</span>
            </div>

            <Button type="submit" className="bg-cyan-500 hover:bg-cyan-600 text-black">
              {editingPost ? 'Update' : 'Create'} Post
            </Button>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4">
        {posts.map((post) => (
          <div key={post.id} className="bg-neutral-900 border border-neutral-800 rounded-xl p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-xl font-semibold text-white">{post.title}</h3>
                  {!post.published && <span className="px-2 py-1 bg-yellow-500/20 text-yellow-300 text-xs rounded">Draft</span>}
                </div>
                <p className="text-neutral-400 text-sm mb-2">{post.excerpt}</p>
                <p className="text-xs text-neutral-500">{post.date} | {post.read_time} | {post.category}</p>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => handleEdit(post)} variant="outline" size="sm"><Edit2 className="w-4 h-4" /></Button>
                <Button onClick={() => handleDelete(post.id)} variant="outline" size="sm" className="border-red-500/50 text-red-400"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BlogManager;
